"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { NewButton } from "@/components/ui/NewButton";

/**
 * Sticky bottom CTA for the Power BI dashboard page.
 * Appears at 50% scroll, hidden while the inline demo form is in view.
 */
export function PowerBiStickyCta() {
  const [isVisible, setIsVisible] = useState(false);
  const [formInView, setFormInView] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (isDismissed) return;

    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const depth = docHeight > 0 ? scrollTop / docHeight : 0;
      setIsVisible(depth >= 0.5);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDismissed]);

  useEffect(() => {
    const form = document.getElementById("book-demo");
    if (!form) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setFormInView(entry.isIntersecting);
      },
      { root: null, threshold: 0.15 }
    );

    observer.observe(form);
    return () => observer.disconnect();
  }, []);

  if (isDismissed || !isVisible || formInView) return null;

  return (
    <div
      className="hidden md:block fixed bottom-0 left-0 right-0 z-50 bg-navy text-white shadow-[0_-2px_12px_rgba(0,0,0,0.18)] motion-reduce:transition-none"
      role="region"
      aria-label="Power BI digital signage call to action"
    >
      <div className="container mx-auto px-8 xl:px-0">
        <div className="flex items-center justify-center gap-6 py-3.5 relative">
          <p className="text-sm md:text-base font-medium text-center text-white">
            Put your Power BI reports on the wall.
          </p>
          <Link href="/book-a-demo">
            <NewButton variant="accent" size="md" arrow="grow">
              Request a Demo
            </NewButton>
          </Link>
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            className="absolute right-0 shrink-0 p-1.5 rounded hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label="Dismiss"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
